import { isEqual } from 'lodash';
import { useCallback, useLayoutEffect, useState } from 'react';

function getDimensionObject(node: HTMLElement): DimensionObject {
  const rect = node.getBoundingClientRect();

  return {
    width: rect.width,
    height: rect.height,
    top: rect.x !== undefined ? rect.x : rect.top,
    left: rect.y !== undefined ? rect.y : rect.left,
    x: rect.x !== undefined ? rect.x : rect.left,
    y: rect.y !== undefined ? rect.y : rect.top,
    right: rect.right,
    bottom: rect.bottom,
  };
}

function useDimensions({
  liveMeasure = true,
  continuousMeasure = false,
}: UseDimensionsArgs = {}): UseDimensionsHook {
  const [dimensions, setDimensions] = useState({});
  const [node, setNode] = useState<HTMLElement>();

  const ref = useCallback((node: HTMLElement) => {
    setNode(node);
  }, []);

  useLayoutEffect(() => {
    if (node) {
      const measure = () => {
        window.requestAnimationFrame(
          () =>
            node &&
            setDimensions((prevDimension) => {
              const newDimension = getDimensionObject(node);
              if (continuousMeasure) {
                return isEqual(prevDimension, newDimension)
                  ? prevDimension
                  : newDimension;
              }
              return newDimension;
            }),
        );
      };
      measure();

      if (liveMeasure && !continuousMeasure) {
        const resizeObserver = new ResizeObserver(() => measure());
        resizeObserver.observe(node);
        window.addEventListener('scroll', measure);

        return () => {
          resizeObserver.disconnect();
          window.removeEventListener('scroll', measure);
        };
      } else if (continuousMeasure) {
        const intervalId = setInterval(() => measure(), 1000);
        return () => {
          clearInterval(intervalId);
        };
      }
    }
  }, [node, liveMeasure, continuousMeasure]);

  return [ref, dimensions as DimensionObject, node];
}

export default useDimensions;

export interface DimensionObject {
  width: number;
  height: number;
  top: number;
  left: number;
  x: number;
  y: number;
  right: number;
  bottom: number;
}

export type UseDimensionsHook = [
  (node: HTMLElement) => void,
  DimensionObject,
  HTMLElement | undefined,
];

export interface UseDimensionsArgs {
  liveMeasure?: boolean;
  continuousMeasure?: boolean;
}
